import * as THREE from 'three';
import { gsap } from 'gsap';
import Lenis from '@studio-freight/lenis';

// --- Three.jsセットアップ ---
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);
camera.up.set(0, 1, 0);

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setClearColor(0x141414);
document.body.appendChild(renderer.domElement);

// --- シェーダーuniforms ---
const uniforms = {
  uSmoothness: { value: 1.0 },
  uGridDensity: { value: 26.0 },
  uNoiseScale: { value: 10.0 },
  uNoiseSpeed: { value: 0.5 },
  uNoiseStrength: { value: 0.15 },
  uEnableDisplacement: { value: true },
  uTime: { value: 0.0 },
  uWireColor: { value: new THREE.Color(0x00ff00) },
  uBaseColor: { value: new THREE.Color(0x141414) }
};

const wireframeMaterial = new THREE.ShaderMaterial({
  uniforms,
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform float uSmoothness;
    uniform float uGridDensity;
    uniform float uNoiseScale;
    uniform float uNoiseSpeed;
    uniform float uNoiseStrength;
    uniform bool uEnableDisplacement;
    uniform float uTime;
    uniform vec3 uWireColor;
    uniform vec3 uBaseColor;

    varying vec2 vUv;

    float random(vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }

    float noise(vec2 st) {
      vec2 i = floor(st);
      vec2 f = fract(st);
      float a = random(i);
      float b = random(i + vec2(1.0,0.0));
      float c = random(i + vec2(0.0,1.0));
      float d = random(i + vec2(1.0,1.0));
      vec2 u = f*f*(3.0-2.0*f);
      return mix(a,b,u.x) + (c - a)*u.y*(1.0-u.x) + (d - b)*u.x*u.y;
    }

    void main() {
      vec2 grid = abs(fract(vUv * uGridDensity - 0.5) - 0.5);
      vec2 gridWidth = fwidth(vUv * uGridDensity);
      float lineX = smoothstep(0.0, gridWidth.x * uSmoothness, grid.x);
      float lineY = smoothstep(0.0, gridWidth.y * uSmoothness, grid.y);
      float line = 1.0 - min(lineX, lineY);
      float noiseValue = 0.0;
      if(uEnableDisplacement) {
        noiseValue = noise(vUv * uNoiseScale + uTime * uNoiseSpeed) * uNoiseStrength;
      }
      vec3 finalColor = mix(uBaseColor, uWireColor, line);
      finalColor += noiseValue;
      gl_FragColor = vec4(finalColor, 1.0);
    }
  `,
  side: THREE.BackSide
});

// --- トーラスノットのパス（ジオメトリ＆カメラ用） ---
class TorusKnotCurve extends THREE.Curve {
  constructor(radius = 20, tube = 6, p = 2, q = 3) {
    super();
    this.radius = radius;
    this.tube = tube;
    this.p = p;
    this.q = q;
  }

  getPoint(t) {
    const TWO_PI = Math.PI * 2;
    const angle = t * TWO_PI;

    const r = this.radius;
    const p = this.p;
    const q = this.q;

    const x = (r + this.tube * Math.cos(q * angle)) * Math.cos(p * angle);
    const y = (r + this.tube * Math.cos(q * angle)) * Math.sin(p * angle);
    const z = this.tube * Math.sin(q * angle);
    return new THREE.Vector3(x, y, z);
  }
}

const path = new TorusKnotCurve(20, 6, 2, 3);

// --- チューブ形状（パスに沿って作成） ---
const geometry = new THREE.TubeGeometry(path, 600, 2, 64, true);
const tube = new THREE.Mesh(geometry, wireframeMaterial);
scene.add(tube);

// --- セクションDOM取得 ---
const sections = document.querySelectorAll('.section');
const sectionRanges = [
  [0.05, 0.15],
  [0.25, 0.35],
  [0.45, 0.55],
  [0.65, 0.75],
  [0.85, 0.95]
];

// --- Lenisセットアップ ---
const lenis = new Lenis({
  smooth: true,
  direction: 'vertical',
  gestureDirection: 'vertical',
  infinite: true,
});

// --- 初期化：カメラとセクション表示をスクロールから算出 ---
window.addEventListener('load', () => {
  const scroll = lenis.scroll;
  const limit = lenis.limit;
  const progress = Math.min(Math.max(scroll / limit, 0), 1);

  const delta = 0.0001;
  const p1 = path.getPoint(progress);
  const p2 = path.getPoint((progress + delta) % 1);

  camera.position.copy(p1);
  camera.lookAt(p2);

  sections.forEach((section, i) => {
    const [start, end] = sectionRanges[i];
    if (progress >= start && progress <= end) {
      gsap.set(section, { autoAlpha: 1, scale: 1 });
    } else {
      gsap.set(section, { autoAlpha: 0, scale: 0.8 });
    }
  });
});

// --- スクロールイベントでカメラとセクション制御 ---
lenis.on('scroll', ({ scroll, limit }) => {
  let progress = scroll / limit;
  progress = Math.min(Math.max(progress, 0), 1);

  const delta = 0.0001;
  const p1 = path.getPoint(progress);
  const p2 = path.getPoint((progress + delta) % 1);

  camera.position.copy(p1);
  camera.lookAt(p2);

  sections.forEach((section, i) => {
    const [start, end] = sectionRanges[i];
    if (progress >= start && progress <= end) {
      gsap.to(section, { autoAlpha: 1, scale: 1, duration: 0.3, ease: "power2.out" });
    } else {
      gsap.to(section, { autoAlpha: 0, scale: 0.8, duration: 0.3, ease: "power2.out" });
    }
  });
});

// --- アニメーションループ ---
function render(time) {
  uniforms.uTime.value += 0.01;
  renderer.render(scene, camera);
  lenis.raf(time);
  requestAnimationFrame(render);
}
requestAnimationFrame(render);

// --- ウィンドウリサイズ ---
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

